import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Grid, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { Service } from '../../../../models/masters/service';
import { servicesApiKeyStore } from '../../../../api/masters/services-api-key-store';
import { MunicipalitySelector } from './municipality-selector';
import { MunicipalityMappingGrid } from './municipality-mapping-grid';
import { useSaveData } from '../../../../hooks/useSaveData';
import { serviceMunicipalityMappingsApiKeyStore } from '../../../../api/masters/service-municipality-mappings-api-key-store';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { UserPermissionName } from '../../../../models/user-permission-name';

const styling = {
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
  buttonPadding: { paddingTop: '20px' },
  tabHeading: { fontWeight: 'bold' },
  buttonStyle: { width: '130px' },
};

interface ContactsDialogProps {
  municipalityID: number;
  open: boolean;
  onClose: (contracts: Service[]) => void;
}

export const ServiceMunicipalityMappingDialog = ({ open, onClose, municipalityID }: ContactsDialogProps) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [selectedMunicipalityID, setMunicipalityID] = useState<number>(municipalityID);
  const { data, isLoading } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceMappingList(0, 0, 0, 0, 0)
  );
  const { isLoading: isSaving, mutateAsync } = useSaveData(
    serviceMunicipalityMappingsApiKeyStore.createServiceMunicipalityMappings()
  );

  const handleServiceSelection = (values: Service[]) => {
    setSelectedServices(values);
  };

  const handleDialogClose = () => {
    onClose(selectedServices);
  };

  const handleMunicipalityChange = (id: number) => {
    setMunicipalityID(id);
  };

  const handleSaveClick = async () => {
    await mutateAsync({
      data: { muncipalityID: selectedMunicipalityID, municipalityServices: selectedServices },
    });
    handleDialogClose();
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth={'xl'}
      // onClose={handleDialogClose}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title" style={styling.dialogTitleStyle}>
        SELECT SERVICES
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{
          position: 'absolute',
          right: 10,
          top: 3,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers style={styling.contentPadding} className="popupDilog">
        <Grid>
          <Grid item>
            <MunicipalitySelector
              municipalityID={selectedMunicipalityID}
              onMunicipalitySectionChange={handleMunicipalityChange}
            />
            <LoadingIndicator isLoading={isLoading || isSaving} />
            {data?.data && (
              <MunicipalityMappingGrid
                allServices={data?.data}
                municipalityID={selectedMunicipalityID}
                onSelectedItems={handleServiceSelection}
              />
            )}
          </Grid>
          <Grid item>
            <div className="popupFooter alignRight">
              <Button
                type={'button'}
                variant="contained"
                disableElevation={true}
                style={styling.buttonStyle}
                disabled={isLoading || isReadOnlyPermission}
                onClick={handleSaveClick}
              >
                SAVE
              </Button>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
